'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function PythagoreanTheoremCalculator() {
  const t = useTranslations("tools.pythagoreanTheoremCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    a: '',
    aSqrt: '',
    b: '',
    bSqrt: '',
    c: '',
    cSqrt: ''
  });

  const [results, setResults] = useState<{
    a: number;
    b: number;
    c: number;
    area: number;
    perimeter: number;
  } | null>(null);

  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#7c3aed',
    accent: '#10b981',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(37, 99, 235, 0.2)',
    errorBg: '#fef2f2',
    errorBorder: '#ef4444',
    errorText: '#991b1b',
  };

  const getValue = (value: string, sqrtValue: string): number | null => {
    const val = parseFloat(value);
    const sqrtVal = parseFloat(sqrtValue);

    if (!isNaN(val) && val > 0) return val;
    if (!isNaN(sqrtVal) && sqrtVal > 0) return Math.sqrt(sqrtVal);
    return null;
  };

  const formatNumber = (num: number): string => {
    return (Math.round(num * 100000) / 100000).toString();
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResults(null);

    let a = getValue(inputs.a, inputs.aSqrt);
    let b = getValue(inputs.b, inputs.bSqrt);
    let c = getValue(inputs.c, inputs.cSqrt);

    const count = [a, b, c].filter(v => v !== null).length;

    if (count < 2) {
      setError(t('errorMinimumValues'));
      return;
    }

    if (count === 3 && a && b && c) {
      const calculated = Math.sqrt(a * a + b * b);
      if (Math.abs(calculated - c) > 0.01) {
        setError(t('errorInvalidTriangle'));
      }
    }

    try {
      if (a === null && b && c) {
        if (c <= b) {
          setError(t('errorHypotenuseGreaterB'));
          return;
        }
        a = Math.sqrt(c * c - b * b);
      } else if (b === null && a && c) {
        if (c <= a) {
          setError(t('errorHypotenuseGreaterA'));
          return;
        }
        b = Math.sqrt(c * c - a * a);
      } else if (c === null && a && b) {
        c = Math.sqrt(a * a + b * b);
      }

      if (!a || !b || !c || isNaN(a) || isNaN(b) || isNaN(c) || a <= 0 || b <= 0 || c <= 0) {
        setError(t('errorInvalidCalculation'));
        return;
      }

      const area = (a * b) / 2;
      const perimeter = a + b + c;

      setResults({ a, b, c, area, perimeter });

    } catch (err) {
      setError(t('errorCalculationFailed'));
    }
  };

  const handleClear = () => {
    setInputs({
      a: '',
      aSqrt: '',
      b: '',
      bSqrt: '',
      c: '',
      cSqrt: ''
    });
    setResults(null);
    setError('');
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, maxWidth: '800px', margin: '0 auto' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 1.5rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '900px', margin: '0 auto', background: 'white', borderRadius: '1rem', overflow: 'hidden' },
    {},
    {},
    {}
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.4rem' },
    { fontSize: '1.6rem' },
    { fontSize: '1.8rem' }
  );

  const calculatorContentStyle = getResponsiveStyle(
    { display: 'grid', gap: '2rem' },
    { gridTemplateColumns: '1fr', padding: '1.5rem' },
    { gridTemplateColumns: '1fr', padding: '2rem' },
    { gridTemplateColumns: '1fr 1fr', padding: '2rem' }
  );

  const calculatorFormStyle: React.CSSProperties = {
    background: '#f8fafc',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    border: '2px solid #e2e8f0'
  };

  const formIntroStyle = getResponsiveStyle(
    { marginBottom: '1.5rem', color: '#475569', lineHeight: 1.6 },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const formGroupStyle: React.CSSProperties = {
    marginBottom: '1.25rem'
  };

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', color: '#334155', fontWeight: 600 },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputWrapperStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap'
  };

  const inputStyle = getResponsiveStyle(
    {
      flex: 1,
      minWidth: '80px',
      padding: '0.75rem',
      border: '2px solid #cbd5e1',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease'
    },
    {},
    {},
    {}
  );

  const sqrtInputStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    flex: 1,
    minWidth: '100px'
  };

  const sqrtSymbolStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    color: colors.primary,
    fontWeight: 700
  };

  const sqrtBoxStyle: React.CSSProperties = {
    position: 'relative',
    flex: 1
  };

  const sqrtBoxBeforeStyle: React.CSSProperties = {
    position: 'absolute',
    top: '-2px',
    left: 0,
    right: 0,
    height: '2px',
    background: colors.primary,
    content: '""'
  };

  const sqrtInputFieldStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #cbd5e1',
    borderTop: 'none',
    borderRadius: '0 0 0.5rem 0.5rem',
    fontSize: '1rem',
    transition: 'all 0.3s ease'
  };

  const buttonGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1rem',
    marginTop: '1.5rem',
    flexWrap: 'wrap'
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      minWidth: '120px',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const clearButtonStyle: React.CSSProperties = {
    flex: 1,
    minWidth: '120px',
    padding: '0.875rem 1.5rem',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    background: '#f1f5f9',
    color: '#475569'
  };

  const triangleVisualStyle = getResponsiveStyle(
    {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderRadius: '0.75rem',
      border: '2px solid #fbbf24'
    },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const triangleDiagramStyle = getResponsiveStyle(
    { position: 'relative' as const, margin: '1rem 0' },
    { width: '220px', height: '180px' },
    { width: '250px', height: '200px' },
    { width: '280px', height: '220px' }
  );

  const formulaDisplayStyle: React.CSSProperties = {
    textAlign: 'center',
    marginTop: '1rem',
    padding: '1rem',
    background: 'white',
    borderRadius: '0.5rem',
    fontSize: '1.1rem',
    color: colors.primary,
    fontWeight: 600
  };

  const errorMessageStyle: React.CSSProperties = {
    background: colors.errorBg,
    border: `2px solid ${colors.errorBorder}`,
    color: colors.errorText,
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    display: error ? 'block' : 'none'
  };

  const resultsStyle: React.CSSProperties = {
    gridColumn: screenSize === 'desktop' ? '1 / -1' : 'auto',
    background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    border: '2px solid #10b981',
    marginTop: '1rem',
    display: results ? 'block' : 'none'
  };

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1rem 0', color: '#065f46', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.3rem' },
    { fontSize: '1.3rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }
  );

  const resultItemStyle: React.CSSProperties = {
    background: 'white',
    padding: '1rem',
    borderRadius: '0.5rem',
    borderLeft: '4px solid #10b981'
  };

  const resultLabelStyle: React.CSSProperties = {
    display: 'block',
    color: '#6b7280',
    fontSize: '0.85rem',
    marginBottom: '0.25rem',
    fontWeight: 500
  };

  const resultValueStyle: React.CSSProperties = {
    display: 'block',
    color: '#065f46',
    fontSize: '1.3rem',
    fontWeight: 700
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)'
    },
    { padding: '1.5rem', marginBottom: '1.5rem' },
    { padding: '2rem', marginBottom: '2rem' },
    { padding: '2.5rem', marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const strongStyle: React.CSSProperties = {
    color: colors.primary,
    fontWeight: 600
  };

  const refLinkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    borderBottom: '1px solid transparent',
    transition: 'border-color 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>📐 {t('toolTitle')}</h2>
          </div>

          <div style={calculatorContentStyle}>
            <div style={calculatorFormStyle}>
              <div style={formIntroStyle}>
                <strong style={strongStyle}>{t('formIntro')}</strong> {t('formIntroDesc')}
              </div>

              <form onSubmit={handleCalculate}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="side-a">{t('sideALabel')}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="side-a"
                      step="any"
                      placeholder={t('enterValue')}
                      value={inputs.a}
                      onChange={(e) => setInputs({...inputs, a: e.target.value})}
                      style={inputStyle}
                    />
                    <span style={{ color: '#94a3b8' }}>{t('or')}</span>
                    <div style={sqrtInputStyle}>
                      <span style={sqrtSymbolStyle}>√</span>
                      <div style={sqrtBoxStyle}>
                        <div style={sqrtBoxBeforeStyle}></div>
                        <input
                          type="number"
                          id="side-a-sqrt"
                          step="any"
                          placeholder={t('sqrtValue')}
                          value={inputs.aSqrt}
                          onChange={(e) => setInputs({...inputs, aSqrt: e.target.value})}
                          style={sqrtInputFieldStyle}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="side-b">{t('sideBLabel')}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="side-b"
                      step="any"
                      placeholder={t('enterValue')}
                      value={inputs.b}
                      onChange={(e) => setInputs({...inputs, b: e.target.value})}
                      style={inputStyle}
                    />
                    <span style={{ color: '#94a3b8' }}>{t('or')}</span>
                    <div style={sqrtInputStyle}>
                      <span style={sqrtSymbolStyle}>√</span>
                      <div style={sqrtBoxStyle}>
                        <div style={sqrtBoxBeforeStyle}></div>
                        <input
                          type="number"
                          id="side-b-sqrt"
                          step="any"
                          placeholder={t('sqrtValue')}
                          value={inputs.bSqrt}
                          onChange={(e) => setInputs({...inputs, bSqrt: e.target.value})}
                          style={sqrtInputFieldStyle}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="side-c">{t('sideCLabel')}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="side-c"
                      step="any"
                      placeholder={t('enterValue')}
                      value={inputs.c}
                      onChange={(e) => setInputs({...inputs, c: e.target.value})}
                      style={inputStyle}
                    />
                    <span style={{ color: '#94a3b8' }}>{t('or')}</span>
                    <div style={sqrtInputStyle}>
                      <span style={sqrtSymbolStyle}>√</span>
                      <div style={sqrtBoxStyle}>
                        <div style={sqrtBoxBeforeStyle}></div>
                        <input
                          type="number"
                          id="side-c-sqrt"
                          step="any"
                          placeholder={t('sqrtValue')}
                          value={inputs.cSqrt}
                          onChange={(e) => setInputs({...inputs, cSqrt: e.target.value})}
                          style={sqrtInputFieldStyle}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div style={buttonGroupStyle}>
                  <button type="submit" style={calculateButtonStyle}>
                    {t('calculateButton')}
                  </button>
                  <button type="button" style={clearButtonStyle} onClick={handleClear}>
                    {t('clearButton')}
                  </button>
                </div>
              </form>

              <div style={errorMessageStyle}>
                {error}
              </div>
            </div>

            <div style={triangleVisualStyle}>
              <div style={triangleDiagramStyle}>
                <svg style={{ width: '100%', height: '100%' }} viewBox="0 0 280 220">
                  <polygon points="40,180 240,180 240,40" fill="#dbeafe" stroke="#2563eb" strokeWidth="3"/>
                  <text x="140" y="200" textAnchor="middle" fill="#2563eb" fontSize="18" fontWeight="bold">a</text>
                  <text x="260" y="110" textAnchor="middle" fill="#2563eb" fontSize="18" fontWeight="bold">b</text>
                  <text x="130" y="100" textAnchor="middle" fill="#7c3aed" fontSize="18" fontWeight="bold">c</text>
                  <path d="M 230,180 L 230,170 L 240,170" fill="none" stroke="#2563eb" strokeWidth="2"/>
                  <circle cx="240" cy="180" r="4" fill="#ef4444"/>
                </svg>
              </div>
              <div style={formulaDisplayStyle}>
                a² + b² = c²
              </div>
            </div>

            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>✅ {t('resultsTitle')}</h3>
              <div style={resultGridStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('resultSideA')}</span>
                  <span style={resultValueStyle}>{results ? formatNumber(results.a) : '-'}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('resultSideB')}</span>
                  <span style={resultValueStyle}>{results ? formatNumber(results.b) : '-'}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('resultSideC')}</span>
                  <span style={resultValueStyle}>{results ? formatNumber(results.c) : '-'}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('resultArea')}</span>
                  <span style={resultValueStyle}>{results ? formatNumber(results.area) : '-'}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('resultPerimeter')}</span>
                  <span style={resultValueStyle}>{results ? formatNumber(results.perimeter) : '-'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Desc')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Desc')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Desc')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Desc')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong style={strongStyle}>{t('step5Title')}</strong> {t('step5Desc')}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph4')}</p>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t('detailsApplicationsTitle')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}><strong style={strongStyle}>{t('appConstruction')}</strong> {t('appConstructionDesc')}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong style={strongStyle}>{t('appNavigation')}</strong> {t('appNavigationDesc')}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong style={strongStyle}>{t('appCarpentry')}</strong> {t('appCarpentryDesc')}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong style={strongStyle}>{t('appEngineering')}</strong> {t('appEngineeringDesc')}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong style={strongStyle}>{t('appEducation')}</strong> {t('appEducationDesc')}</li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t('detailsBestPracticesTitle')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>{t('practice1')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('practice2')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('practice3')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('practice4')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('practice5')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginBottom: '0.5rem' }}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginBottom: '0.5rem' }}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginBottom: '0.5rem' }}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginBottom: '0.5rem' }}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.1rem' : '1.25rem', marginBottom: '0.5rem' }}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.graphcalc.com/pythagorean-theorem-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref1')}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.free-power-point-templates.com/articles/pythagorean-theorem-calculator-formula-and-applications/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref2')}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://byjus.com/pythagorean-theorem-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref3')}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.homebuildingandrepairs.com/math/3_4_5_rule.html" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref4')}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.calculatorsoup.com/calculators/geometry-plane/pythagorean-theorem.php" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref5')}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.calculator.net/pythagorean-theorem-calculator.html" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref6')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}